<script setup>
import { usePage } from '@inertiajs/vue3'
import { useElementSize } from '@vueuse/core'
import { intlFormatDistance } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFeature } from '@/composables/useFeature'
import { useFormatDate } from '@/composables/useFormatDate'
import { useScrollShortcuts } from '@/composables/useScrollShortcuts'
import { DETAIL_DATE_FIELDS } from '@/constants'
import { useTransactionStore } from '@/stores/transaction'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEsgStore } from '@/stores/useEsgStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import { useInfraStore } from '@/stores/useInfraStore'
import DataTab from '../components/detail/DataTab.vue'
import DetailLinksComponent from '../components/detail/data/DetailLinksComponent.vue'
import DetailMarketOverviewChoice from '../components/detail/data/DetailMarketOverviewChoice.vue'
import DetailTemplateComponent from '../components/detail/data/DetailTemplateComponent.vue'
import SectionNavigatorDropdown from '../components/detail/SectionNavigatorDropdown.vue'
import EsgTab from '../components/detail/EsgTab.vue'
import { useDetailLayoutResizeObservers } from '../composables/useDetailLayoutResizeObservers'
import { useExportDetail } from '../composables/useExportDetail'
import AppLayout from '../Layouts/AppLayout.vue'

const { formatISOToDatePicker } = useFormatDate()

const store = useDetailStore()
const transactionStore = useTransactionStore()
const evaluationStore = useEvaluationStore()
const esgStore = useEsgStore()
const infraStore = useInfraStore()

const {
  mainForm,
  ehrId,
  cadasterId,
  evaluationTemplateId,
  exportMapUnits,
  exportMapUnitsReady,
  exportPhase,
  exportUploadProgress,
} = storeToRefs(store)

const { layoutStyle } = useDetailLayoutResizeObservers()

const { executeExport } = useExportDetail()

const { t } = useI18n()

const handleBeforeUnload = (e) => {
  if (evaluationStore.isDirty) {
    e.preventDefault()
  }
}

const props = defineProps({
  evaluationId: {
    type: Number,
    required: true,
  },
  objectId: {
    type: String,
    required: true,
  },
  ehrCode: {
    type: String,
    required: false,
    default: null,
  },
  cadasterId: {
    type: String,
    required: false,
    default: null,
  },
  adsOid: {
    type: String,
    required: false,
    default: null,
  },
  objectType: {
    type: String,
    required: false,
    default: null,
  },
  propertyData: {
    type: Object,
    default: () => {},
  },
  templates: {
    type: Array,
    default: () => [],
  },
  evaluation: {
    type: Object,
    default: () => {},
  },
  similarityDefaults: {
    type: Object,
    default: null,
  },
  similarityProfiles: {
    type: Array,
    default: () => [],
  },
  isLandParcel: {
    type: Boolean,
    default: false,
  },
})

// Land parcels (RED-265) render no EHR sections, so the section navigator
// only offers the ones that exist. Below defineProps deliberately — the
// composable needs the prop at setup time.
const {
  sections,
  activeSectionId,
  scrollToSection,
  refresh: refreshScrollShortcuts,
} = useScrollShortcuts(
  props.isLandParcel
    ? { enabledSections: ['section-main', 'section-maps', 'section-transactions'] }
    : {},
)

const tabIndex = ref(0)

// RED-317: the Turuülevaade choice exists only beside a universal mall (the
// one carrying the ${turuylevaade} code) and only while `market-overviews` is
// on. The server withholds the prop without the flag; the client mirrors the
// flag so the row goes the moment it flips.
const page = usePage()
const marketOverviewsEnabled = useFeature('market-overviews')
const marketOverviewVisible = computed(() => {
  const template = props.templates.find(item => item.id === evaluationTemplateId.value)
  return marketOverviewsEnabled.value
    && Boolean(template?.is_universal)
    && page.props.marketOverviewChoice != null
})
const { width: exportBlockWidth } = useElementSize(useTemplateRef('exportBlock'))

// Refresh scroll shortcuts when switching to DataTab
watch(tabIndex, newIndex => {
  if (newIndex === 0) {
    // Give the DOM time to render before refreshing observers
    nextTick(() => {
      refreshScrollShortcuts()
    })
  }
})

// ── Unified export progress (subheader bottom-edge bar) ────────────────
// Visible ONLY while an export the user started is running — background ESG
// capture stays invisible until Ekspordi is clicked. Phases share one bar:
// waiting for maps 0–40%, upload 40–70%, server processing indeterminate,
// done flashes 100%. The phase text + counter live in the Ekspordi button
// itself (DetailTemplateComponent); this bar is the pulse.
const exportProgressVisible = computed(() => exportPhase.value !== 'idle')

const exportProgressMode = computed(() =>
  exportPhase.value === 'processing' ? 'indeterminate' : 'determinate',
)

const exportProgressValue = computed(() => {
  if (exportPhase.value === 'done') return 100
  if (exportPhase.value === 'uploading') {
    return Math.round(40 + exportUploadProgress.value * 0.3)
  }
  const mapShare = exportMapUnitsReady.value / exportMapUnits.value
  return Math.round(mapShare * 40)
})

const now = ref(Date.now())

const lastSavedAt = computed(() => {
  return intlFormatDistance(
    new Date(evaluationStore.lastSavedAt),
    new Date(now.value),
    {
      addSuffix: true,
      locale: 'et',
    },
  )
})

let intervalId = null

watch(
  () => props.propertyData,
  async newVal => {
    await store.setPropertyData(props.cadasterId, newVal)
    // Restore saved ESG user edits after ESG data is fetched and auto-initialized
    if (props.evaluation.esgData) {
      esgStore.restoreFromSaved(props.evaluation.esgData)
    }
  },
)

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload)

  intervalId = setInterval(() => {
    now.value = Date.now()
  }, 1000)

  if (store.evaluationId !== props.evaluationId) {
    store.$reset()
  }

  // Pinia outlives an Inertia visit, so /evaluation/13 → /evaluation/14
  // remounts this page on top of evaluation 13's stores. Drop the outgoing
  // evaluation's state before restoring the incoming one, or its comparables
  // stay in selectionTableRows and the kohandustabel shows both sets (6
  // comparable columns instead of 3). Runs while evaluationStore.id still
  // holds the outgoing id so its pending autosave flushes to the right row.
  if (evaluationStore.id !== props.evaluationId) {
    evaluationStore.resetForEvaluation()
    transactionStore.resetForEvaluation()
    esgStore.resetForEvaluation()
  }

  evaluationStore.id = props.evaluationId
  evaluationStore.status = props.evaluation.status
  evaluationStore.adsOid = props.adsOid
  evaluationStore.objectType = props.objectType
  evaluationStore.isLandParcel = props.isLandParcel
  // RED-279: seed the similarity controls from the calibrated defaults (once per session)
  evaluationStore.applySimilarityDefaults(props.similarityDefaults)
  // Saved profiles for the popover selector; also resolves the initial selection.
  evaluationStore.setSimilarityProfiles(props.similarityProfiles)

  if (props.evaluation.lastSavedAt) {
    evaluationStore.lastSavedAt = props.evaluation.lastSavedAt
  }

  if (props.evaluation.fields) {
    Object.entries(props.evaluation.fields).forEach(([key, value]) => {
      store[key] = value
    })
  }

  if (props.evaluation.mainForm) {
    Object.entries(props.evaluation.mainForm).forEach(([key, value]) => {
      if (DETAIL_DATE_FIELDS.includes(key)) {
        store.mainForm[key] = formatISOToDatePicker(value)
      } else {
        store.mainForm[key] = value
      }
    })
  }

  if (props.evaluation.technical) {
    Object.entries(props.evaluation.technical).forEach(([key, value]) => {
      store.ehrTechnical[key] = value
    })
  }

  if (props.evaluation.construction) {
    Object.entries(props.evaluation.construction).forEach(([key, value]) => {
      store.ehrConstruction[key] = value
    })
  }

  if (props.evaluation.communal) {
    Object.entries(props.evaluation.communal).forEach(([key, value]) => {
      store.ehrCommunal[key] = value
    })
  }

  if (props.evaluation.interior) {
    Object.entries(props.evaluation.interior).forEach(([key, value]) => {
      store.interior[key] = value
    })
  }

  // Restore saved infrastructure data (POI descriptions, location info)
  if (props.evaluation.infraData) {
    infraStore.restoreFromSaved(props.evaluation.infraData)
  }

  // Restore replacedComparableIds (needed for both flows)
  if (props.evaluation.comparisonTableState?.replacedComparableIds) {
    evaluationStore.initReplacedComparableIds(
      props.evaluation.comparisonTableState.replacedComparableIds,
    )
  }

  // Manual flow: restore marked transactions, then comparison page AFTER reset completes
  if (props.evaluation.marked && !props.evaluation.prediction) {
    nextTick(() => {
      transactionStore.resetMarkedTransactions(false)
      transactionStore.resetComparableState().then(() => {
        transactionStore.restoreMarkedTransactions(props.evaluation.marked)
        // Restore comparison page AFTER reset (reset sets page to 0)
        if (props.evaluation.comparisonTableState?.comparisonComponentPage) {
          transactionStore.setComparisonPage(
            props.evaluation.comparisonTableState.comparisonComponentPage,
          )
        }
        if (props.evaluation.comparisonTableState?.customCriteria) {
          transactionStore.restoreCustomCriteria(
            props.evaluation.comparisonTableState.customCriteria,
          )
        }
        if (props.evaluation.comparisonTableState?.selectedPriceBase) {
          transactionStore.restorePriceMode(
            props.evaluation.comparisonTableState.selectedPriceBase,
            props.evaluation.comparisonTableState.selectedUnitPriceBase,
          )
        }
        if (props.evaluation.comparisonTableState?.hiddenGroups) {
          transactionStore.restoreHiddenGroups(
            props.evaluation.comparisonTableState.hiddenGroups,
          )
        }
      })
    })
  }

  // Prediction flow: restore comparison page then init from saved prediction
  if (props.evaluation.prediction) {
    if (props.evaluation.comparisonTableState?.comparisonComponentPage) {
      transactionStore.setComparisonPage(
        props.evaluation.comparisonTableState.comparisonComponentPage,
      )
    }
    if (props.evaluation.comparisonTableState?.customCriteria) {
      transactionStore.restoreCustomCriteria(
        props.evaluation.comparisonTableState.customCriteria,
      )
    }
    if (props.evaluation.comparisonTableState?.selectedPriceBase) {
      transactionStore.restorePriceMode(
        props.evaluation.comparisonTableState.selectedPriceBase,
        props.evaluation.comparisonTableState.selectedUnitPriceBase,
      )
    }
    if (props.evaluation.comparisonTableState?.hiddenGroups) {
      transactionStore.restoreHiddenGroups(
        props.evaluation.comparisonTableState.hiddenGroups,
      )
    }
    nextTick(() => {
      evaluationStore
        .initFromSavedPrediction(props.evaluation.prediction, props.evaluation.marked || [])
        .catch(err => console.error('Failed to restore saved prediction', err))
    })
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
  if (intervalId) clearInterval(intervalId)
})
</script>

<template>
  <AppLayout>
    <template #subheader>
      <div class="detail-subheader relative w-full px-4 py-2 bg-white dark:bg-gray-800 shadow-md">
        <!-- Unified export progress: ESG capture → upload → server render.
             Pinned absolutely to the bottom edge so it is layout-neutral —
             a flex child here overlaps the centred tabs at 1280px (RED-291). -->
        <ProgressBar
          v-if="exportProgressVisible"
          data-testid="detail-export-progress-bar"
          :mode="exportProgressMode"
          :value="exportProgressValue"
          :show-value="false"
          class="absolute inset-x-0 bottom-0 pointer-events-none"
          style="height: 3px"
        />
        <div class="relative flex items-center justify-between">
          <!-- Left: Registry Links -->
          <div class="shrink-0">
            <DetailLinksComponent
              :cadaster-id="cadasterId"
              :ehr-id="ehrId"
            />
          </div>

          <!-- Center: Tabs (always centered) -->
          <div class="absolute left-1/2 -translate-x-1/2">
            <div class="tab-switcher px-1 py-0.5 inline-flex rounded-lg bg-gray-100 dark:bg-gray-700 p-0.5">
              <button
                type="button"
                data-testid="detail-tab-data"
                class="tab-button px-2 py-1 text-sm font-medium rounded-md transition-all duration-200"
                :class="{
                  'bg-primary-500 dark:bg-gray-600 text-white dark:text-white shadow-xs': tabIndex === 0,
                  'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white': tabIndex !== 0,
                }"
                @click="tabIndex = 0"
              >
                {{ $t('messages.detail.tabs.detailData') }}
              </button>
              <button
                type="button"
                data-testid="detail-tab-esg"
                class="tab-button px-2 py-1 text-sm font-medium rounded-md transition-all duration-200"
                :class="{
                  'bg-primary-500 dark:bg-gray-600 text-white dark:text-white shadow-xs': tabIndex === 1,
                  'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white': tabIndex !== 1,
                }"
                @click="tabIndex = 1"
              >
                {{ $t('messages.detail.tabs.esg') }}
              </button>
            </div>
          </div>

          <!-- Section Navigator (positioned after tabs, hidden on ESG tab) -->
          <div
            class="absolute left-1/2 ml-[120px] flex items-center transition-opacity duration-150"
            :class="{
              'opacity-100': tabIndex === 0,
              'opacity-0 pointer-events-none': tabIndex !== 0
            }"
          >
            <div class="w-px h-5 bg-gray-300 dark:bg-gray-600 mr-3" />
            <SectionNavigatorDropdown
              :sections="sections"
              :active-section-id="activeSectionId"
              :scroll-to-section="scrollToSection"
            />
          </div>

          <!-- Right: Template Export -->
          <div ref="exportBlock" class="shrink-0">
            <DetailTemplateComponent :templates="templates" @export="executeExport" />
          </div>
        </div>
        <!-- RED-317 Turuülevaade choice for a universal mall. Its own row, so
             the links and the centred tabs above keep their place (e2e 04 and
             36 hit-test them); it takes the export block's width, so its select
             sits exactly under the mall select. -->
        <div v-if="marketOverviewVisible" class="mt-2 flex justify-end">
          <DetailMarketOverviewChoice :style="{ width: `${exportBlockWidth}px` }" />
        </div>
      </div>
    </template>

    <!-- Main Content (full width, no aside) -->
    <div id="detail-container" data-testid="detail-container" class="flex-1 dark:bg-gray-900" :style="layoutStyle.container">
      <div class="relative mt-4 mb-10 px-8">
        <div class="dark:bg-gray-900">
          <div v-show="tabIndex === 0">
            <DataTab
              :object-id="objectId"
              :ehr-code="ehrCode"
              :is-land-parcel="isLandParcel"
              :evaluation-approver="evaluation.approvers"
            />
          </div>
          <div :class="{ 'esg-tab-offscreen': tabIndex !== 1 }">
            <EsgTab :cadaster-id="cadasterId" />
          </div>
        </div>
      </div>
    </div>

    <!-- Fixed Last Saved Indicator -->
    <div class="last-saved-indicator fixed bottom-4 left-4 z-40 pointer-events-none select-none">
      <div class="flex items-center gap-2 px-3 py-2 bg-primary-50/70 dark:bg-gray-800/90 backdrop-blur-xs rounded-lg border border-gray-200 dark:border-gray-700 shadow-xs">
        <span class="pi pi-save text-xs text-gray-400 dark:text-gray-500" />
        <div class="flex flex-col">
          <span class="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            Viimati salvestatud
          </span>
          <span class="text-xs text-gray-600 dark:text-gray-300 font-medium">
            {{ lastSavedAt }}
          </span>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<style scoped>
.detail-subheader {
  border-bottom: 1px solid rgb(229 231 235 / 0.5);
}

:deep(.dark) .detail-subheader {
  border-bottom-color: rgb(55 65 81 / 0.5);
}

.esg-tab-offscreen {
  position: fixed;
  left: -99999px;
  width: 100vw;
  visibility: hidden;
  pointer-events: none;
}

.tab-switcher {
  box-shadow: inset 0 1px 2px rgb(0 0 0 / 0.05);
}

.tab-button {
  min-width: 80px;
}

.last-saved-indicator {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
